import { Injectable, inject } from '@angular/core';
import { ISbRichtext, ISbStoryData } from '@storyblok/js';
import { firstValueFrom } from 'rxjs';
import { customRenderText, fixHtml, removeHtml } from '../storyblok/custom-render-text';
import { Rating, RatingsService } from './ratings.service';
import { StoryblokHelperService } from './storyblok-helper.service';
import { StoryblokReplaceService } from './storyblok-replace.service';
import { TransferStateService } from './transfer-state.service';

export type Schema = string | number | Schema[] | { [key: string]: Schema };

interface RawSchemaItemText {
  key: string;
  component: 'SchemaItemText';
  value: string;
}

interface RawSchemaItemNumber {
  key: string;
  component: 'SchemaItemNumber';
  value: number;
}

interface RawSchemaItemArray {
  key: string;
  component: 'SchemaItemArray';
  items: RawSchemaItem[];
}

interface RawSchemaItemObject {
  key: string;
  component: 'SchemaItemObject';
  items: RawSchemaItem[];
  ifKeyHasValue?: string;
  dynamicArrayFrom?: string;
}

interface RawSchemaItemDate {
  key: string;
  component: 'SchemaItemDate';
  value: string;
}

type RawSchemaItem =
  | RawSchemaItemText
  | RawSchemaItemNumber
  | RawSchemaItemArray
  | RawSchemaItemObject
  | RawSchemaItemDate;

@Injectable({
  providedIn: 'root',
})
export class StoryblokSchemaService {
  private ratingService = inject(RatingsService);
  private storyblokHelperService = inject(StoryblokHelperService);
  private storyblokReplaceService = inject(StoryblokReplaceService);
  private transferState = inject(TransferStateService);

  private replaceSchemaStoryValues(
    story: ISbStoryData,
    value: string,
    relationships: ISbStoryData[],
    ratings?: Rating[]
  ): string | number {
    const regex = /{{(.*?)}}/g;
    let match: string[];
    while ((match = regex.exec(value))) {
      const path = match[1].split('.');
      const replaceVal = this.getReplaceVal(story, path, relationships);
      if (replaceVal && ['string', 'number'].includes(typeof replaceVal)) {
        value = value.replace(match[0], replaceVal as unknown as string);
      }
    }

    // RATING[RATING[schemaTestPageRating,best]]
    const regex2 = /RATING\[(.*?)\]/g;
    let match2: string[];
    while ((match2 = regex2.exec(value))) {
      const [ratingName, ratingType] = match2[1].split(',');
      const rating = this.ratingService.calcRating(ratings, ratingName);
      if (!rating) continue;
      const ratingValue = rating[ratingType];
      if (ratingValue !== undefined) {
        value = value.replace(match2[0], ratingValue.toString());
      }
    }

    if (!value.includes('{{')) return value;
    return removeHtml(fixHtml(this.storyblokReplaceService.replaceText(value)));
  }

  private getSchemaValue = (
    item: RawSchemaItem,
    story: ISbStoryData,
    relationships: ISbStoryData[],
    ratings?: Rating[]
  ): Schema => {
    if (item.component === 'SchemaItemText') {
      // item.value might be "prefix: {{story.content.title}} suffix" for example
      // so we need to replace the {{story.content.title}} part with the actual value
      return this.replaceSchemaStoryValues(story, item.value, relationships, ratings);
    } else if (item.component === 'SchemaItemNumber') {
      return Number(item.value);
    } else if (item.component === 'SchemaItemObject') {
      if (item.ifKeyHasValue) {
        if (item.ifKeyHasValue.startsWith('{{COMPONENT[')) {
          const componentType = item.ifKeyHasValue.split('[')[1].split(']')[0];
          const hasAtLeastOne =
            this.storyblokHelperService.getBlocks(story.content.body, [componentType], true).length > 0;
          if (!hasAtLeastOne) return;
        } else {
          const value = this.replaceSchemaStoryValues(story, item.ifKeyHasValue, relationships, ratings) as string;
          if (!value || value.includes('{') || value.includes('[')) return;
        }
      }

      if (item.dynamicArrayFrom) {
        return this.getSchemaDynamicArray(item, story, relationships, ratings);
      }

      return this.getSchemaObject(item.items, {}, story, relationships, ratings);
    } else if (item.component === 'SchemaItemArray') {
      return this.getSchemaArray(item.items, [], story, relationships, ratings);
    } else if (item.component === 'SchemaItemDate') {
      // 2023-12-21 23:31
      const year = Number(item.value.slice(0, 4));
      const month = Number(item.value.slice(5, 7)) - 1;
      const day = Number(item.value.slice(8, 10));
      const hour = Number(item.value.slice(11, 13));
      const minute = Number(item.value.slice(14, 16));
      return new Date(year, month, day, hour, minute).toISOString();
    }
  };

  private getSchemaArray = (
    schemaItems: RawSchemaItem[],
    array: Schema[],
    story: ISbStoryData,
    relationships: ISbStoryData[],
    ratings?: Rating[]
  ): Schema[] => {
    schemaItems.forEach(item => {
      const value = this.getSchemaValue(item, story, relationships, ratings);
      if (value === undefined) return;
      array.push(value);
    });
    return array;
  };

  private getSchemaObject = (
    schemaItems: RawSchemaItem[],
    object: Schema,
    story: ISbStoryData,
    relationships: ISbStoryData[],
    ratings?: Rating[]
  ): Schema => {
    schemaItems.forEach(item => {
      const value = this.getSchemaValue(item, story, relationships, ratings);
      if (value === undefined) return;
      object[item.key] = value;
    });
    return object;
  };

  private getSchemaDynamicArray = (
    item: RawSchemaItemObject,
    story: ISbStoryData,
    relationships: ISbStoryData[],
    ratings?: Rating[]
  ): Schema[] => {
    if (item.dynamicArrayFrom.includes('COMPONENT[')) {
      const regex = new RegExp(/COMPONENT\[(.*?)\]/g);
      const componentName = regex.exec(item.dynamicArrayFrom)[1];

      const blocks = this.storyblokHelperService.getBlocks(story.content.body, [componentName]);
      const rest = `${item.dynamicArrayFrom.slice(regex.lastIndex + 1)}`.split('.').map(s => s.replaceAll('}', ''));

      const items = blocks
        .map(block => {
          let current = block;
          for (const key of rest) {
            current = current[key];
          }
          return current;
        })
        .flat()
        .map(blokItem => {
          const value = this.getSchemaValue({ ...item, dynamicArrayFrom: undefined }, blokItem, relationships, ratings);
          if (value === undefined) return;
          return value;
        });

      return items.length ? items : undefined;
    }

    const parts = item.dynamicArrayFrom.split('.');

    let current = story;
    for (const key of parts) {
      current = current[key];
    }
    if (!current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (current as any).map(blokItem => {
      const value = this.getSchemaValue({ ...item, dynamicArrayFrom: undefined }, blokItem, relationships, ratings);
      if (value === undefined) return;
      return value;
    });
  };

  public getSchema = async (story: ISbStoryData, relationships: ISbStoryData[]): Promise<Schema> => {
    return this.transferState.withStateTransfer(`schema-${story.uuid}`, async () => {
      const schema = relationships.find(re => re.uuid === story.content.schema)?.content as {
        requiresCustomRatings: boolean;
        items: RawSchemaItem[];
      };

      if (!schema) return;

      const ratings = schema.requiresCustomRatings
        ? await firstValueFrom(this.ratingService.getCustomRatings())
        : undefined;

      const schemaObject = this.getSchemaObject(schema.items, {}, story, relationships, ratings);
      const isMultiSchemaArray = Object.keys(schemaObject).length === 1 && Array.isArray(schemaObject['']);
      if (isMultiSchemaArray) {
        const schemaArray = schemaObject[''];
        schemaArray.forEach(item => (item['@context'] = 'https://schema.org/'));
        return schemaArray;
      }

      schemaObject['@context'] = 'https://schema.org/';
      return schemaObject;
    });
  };

  public getPreviewSchema = (story: ISbStoryData): Schema => {
    if (!story.content.items) return {};
    return this.getSchemaObject(
      story.content.items,
      {
        '@context': 'https://schema.org/',
      },
      story,
      []
    );
  };

  private getReplaceVal(story: ISbStoryData, path: string[], relationships: ISbStoryData[]) {
    let current: ISbStoryData | string | ISbStoryData['content'] = story;

    for (const key of path) {
      current = current[key];
      const isUuid =
        typeof current === 'string' &&
        current.match(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/);
      if (isUuid) {
        const relationship = relationships.find(re => re.uuid === current);
        if (relationship) current = relationship.content;
      }

      if (typeof current === 'object' && current?.['type'] === 'doc') {
        current = removeHtml(fixHtml(customRenderText(current as ISbRichtext)));
      }

      if (!current) break;
    }
    return current;
  }
}
